/**
 * V2 Constants and Utilities
 * Shared data and helper functions for V2 components
 */

// All possible product sizes
export const ALL_SIZES = ['3ml', '5ml', '10ml', '30ml', '50ml', '100ml'];

/**
 * Get the price for a specific size of a product.
 * Returns null if the size has no valid price (null, undefined, 0, or negative).
 * NEVER falls back to another size's price.
 * @param {Object} product - Product object with price_Xml fields
 * @param {string} size - Size string like '3ml', '50ml'
 * @returns {number|null} - The price or null if not available
 */
export const getSizePrice = (product, size) => {
  if (!product || !size) return null;
  const normalizedSize = size.toLowerCase();
  const priceKey = `price_${normalizedSize}`;
  const price = product[priceKey];
  // Only return price if it's a positive number
  if (typeof price === 'number' && price > 0) {
    return price;
  }
  return null;
};

/**
 * Get array of available sizes for a product (only sizes with valid prices).
 * Optionally filters out out-of-stock sizes.
 * @param {Object} product - Product object
 * @param {boolean} checkStock - If true, also filters out sizes with stock <= 0
 * @returns {Array<{size: string, price: number}>} - Array of {size, price} objects
 */
export const getAvailableSizes = (product, checkStock = false) => {
  if (!product) return [];
  
  return ALL_SIZES.map(size => {
    const price = getSizePrice(product, size);
    if (!price) return null;
    
    if (checkStock) {
      const stockKey = `stock_${size}`;
      const stock = product[stockKey];
      // If stock is explicitly 0, exclude it. null/undefined means unlimited.
      if (stock !== undefined && stock !== null && stock <= 0) {
        return null;
      }
    }
    
    return { size, price };
  }).filter(Boolean);
};

/**
 * Stock rules (mirrors backend/stock_sync.py): a size is sold out when its stock is 0 or less
 * (no figure = unlimited); a product is sold out when every priced size is, or an admin marked it Out of Stock.
 */
export const isSizeSoldOut = (product, size) => {
  if (!product) return false;
  if (product.status === 'out_of_stock') return true;
  const stock = product[`stock_${String(size).toLowerCase()}`];
  return stock !== undefined && stock !== null && stock <= 0;
};

export const isProductSoldOut = (product) => {
  if (!product) return false;
  if (typeof product.sold_out === 'boolean') return product.sold_out;
  if (product.status === 'out_of_stock') return true;
  const priced = ALL_SIZES.filter((s) => getSizePrice(product, s));
  return priced.length > 0 && priced.every((s) => isSizeSoldOut(product, s));
};

/**
 * Get the lowest available price for a product (for "FROM ₹X" display).
 * Only considers sizes with valid prices.
 * @param {Object} product - Product object
 * @returns {number|null} - Lowest price or null if no valid prices
 */
export const getLowestPrice = (product) => {
  const availableSizes = getAvailableSizes(product);
  if (availableSizes.length === 0) return null;
  return Math.min(...availableSizes.map(s => s.price));
};

// Olfactive families data
export const FAMILIES = [
  { name: "Gourmand", description: "Warm · sweet · dessert-led" },
  { name: "Leather & Tobacco", description: "Smoky · dry · grounded" },
  { name: "Woody", description: "Earthy · deep · structured" },
  { name: "Amber", description: "Resinous · golden · slow" },
  { name: "Oriental", description: "Spiced · opulent · layered" },
  { name: "Fresh", description: "Herbal · crisp · clean" },
  { name: "Floral", description: "Petal-forward · soft · airy" },
  { name: "Aquatic", description: "Marine · cool · transparent" },
  { name: "Green", description: "Leafy · sharp · verdant" },
  { name: "Spicy", description: "Warm · piquant · bold" },
  { name: "Chypre", description: "Mossy · classic · refined" },
  { name: "Fougère", description: "Herbal · barbershop · sharp" },
  { name: "Fruity", description: "Juicy · bright · playful" },
  { name: "Exclusive Line", description: "Rare · limited · signature" },
];

// Default hero slides
export const DEFAULT_HERO_SLIDES = [
  {
    eyebrow: "Independence Day Offer",
    heading: "15% off on orders above ₹1,000. Orders above ₹7,900 get 17.5% off",
    cta: { text: "Explore the collection", link: "/v2/shop" },
    image: "/images/v2/hero-independence.png"
  },
  {
    eyebrow: "New Launch",
    heading: "Introducing Royal Lather",
    subheading: "Inspired by the grooming rituals of powerful monarchs",
    cta: { text: "Explore", link: "/v2/products/royal-lather" },
    image: "/images/v2/hero-royal-lather.png"
  },
  {
    eyebrow: "New Launch",
    heading: "Introducing Dense Boscage",
    subheading: "Take a walk deep into the forest",
    cta: { text: "Explore", link: "/v2/products/dense-boscage" },
    image: "/images/v2/hero-dense-boscage.png"
  },
  {
    eyebrow: "Mridul's Fragrances · Handcrafted in India",
    heading: "Compositions that arrive with grace and leave something worth remembering",
    cta: { text: "Explore the collection", link: "/v2/shop" },
    image: "/images/v2/hero-compositions.png"
  }
];

// Journal articles
export const JOURNAL_ARTICLES = [
  {
    tag: "Fragrance 101",
    title: "How top, heart and base notes actually work",
    description: "A short explainer for anyone new to reading a fragrance pyramid."
  },
  {
    tag: "Behind the scent",
    title: "Where Cinnagum's cinnamon comes from",
    description: "Sourcing notes and the reasoning behind key raw materials."
  },
  {
    tag: "Guides",
    title: "A simple layering guide for your first two scents",
    description: "Practical pairing suggestions across the current collection."
  }
];

// Format number with Indian locale (commas)
export const formatCurrency = (num) => {
  if (!num) return '2,000';
  return Number(num).toLocaleString('en-IN');
};

// Get product image - use actual uploaded images from database
export const getProductImage = (product) => {
  return product.primary_image || product.images?.[0] || '/images/placeholder-product.jpg';
};

// Get family image - use products from that family if available
export const getFamilyImage = (index, products) => {
  if (products && products.length > 0) {
    const product = products[index % products.length];
    return product?.primary_image || product?.images?.[0] || '/images/placeholder-product.jpg';
  }
  return '/images/placeholder-product.jpg';
};

// Helper to determine theme from time
export const getThemeFromTime = () => {
  const hour = new Date().getHours();
  // Light mode from 6 AM to 6 PM
  return (hour >= 6 && hour < 18) ? 'light' : 'dark';
};

// Fonts (Lora + Host Grotesk) are loaded from Google Fonts in public/index.html.
// Kept as an empty component so existing <FontStyles /> usages keep working.
export const FontStyles = () => null;
